import { BackendApplication, BackendApplicationRevision, RemovedBackendApplication } from '@/types/backend';

export const application = (
  baseUrl: string,
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>
) => {
  return {
    /**
     * Get the latest used applications.
     * @param count How many applications (max) to get
     * @returns
     */
    latest: async (count?: number): Promise<BackendApplication[] | null> => {
      const url = new URL(`${baseUrl}/Latest`);
      const params = new URLSearchParams();
      if (count) {
        params.append('count', count.toString());
        url.search = params.toString();
      }
      const response = await fetchWithAuth(url.toString());
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.debug('application.latest response', data);
      return data;
    },

    /**
     * Get all applications.
     * @returns
     */
    list: async (): Promise<BackendApplication[] | null> => {
      const url = `${baseUrl}/List`;
      const response = await fetchWithAuth(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.debug('application.list response', data);
      return data;
    },

    /**
     * Get removed applications.
     * @returns
     */
    listRemoved: async (): Promise<RemovedBackendApplication[] | null> => {
      const url = `${baseUrl}/ListRemoved`;
      const response = await fetchWithAuth(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.debug('application.listRemoved response', data);
      return data;
    },

    /**
     * Get application revision history
     * @returns
     */
    history: async (applicationId: string): Promise<BackendApplicationRevision[] | null> => {
      const url = new URL(`${baseUrl}/History`);
      const params = new URLSearchParams();
      params.append('ApplicationId', applicationId);
      url.search = params.toString();
      const response = await fetchWithAuth(url.toString());
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.debug('application.history response', data);
      return data;
    },

    /**
     * Get application block data in JSON form. Defaults to latest revision if no revision is given.
     * @param appId Application GUID
     * @param revision Revision number, default (null) to latest
     * @returns
     */
    load: async (applicationId: string, revision?: number): Promise<BackendApplicationRevision | null> => {
      const url = new URL(`${baseUrl}/Load`);
      const params = new URLSearchParams();
      params.append('ApplicationId', applicationId);
      revision && params.append('Revision', revision.toString());
      url.search = params.toString();
      const response = await fetchWithAuth(url.toString());
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.debug('application.load response', data);
      return data;
    },

    /**
     * Create a new application.
     * @param name Application Name (used by telephony)
     * @param label Application Label (used by web)
     * @param description Application Description
     * @param folderId Folder to place Application in
     * @param blockData Application Data, as JSON string
     * @param comment Comment for Application Revision
     * @returns
     */
    create: async (
      name: string,
      label: string | null,
      description: string | null,
      folderId: number,
      blockData: string,
      comment: string
    ): Promise<{ applicationId: string; revision: number }> => {
      const url = `${baseUrl}/Create`;
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, label, description, folderId, blockData, comment })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.debug('application.create response', data);
      return data;
    },

    /**
     * Save application block data as JSON string.
     * @param applicationId Application GUID
     * @param blockData Application Data, as JSON string
     * @param comment Comment for Application Revision
     * @returns Revision as a number value
     */
    save: async (applicationId: string, blockData: string, comment: string) => {
      const url = `${baseUrl}/Save`;
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ applicationId, blockData, comment })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.debug('application.save response', data);
      return data;
    },

    /**
     * Copy given application to given folder. Target specific revision, default (null) is latest.
     * @param applicationId Application GUID
     * @param targetFolderId Folder to place Application copy
     * @param revision Revision to copy, default (null) to latest
     * @returns
     */
    copy: async (applicationId: string, targetFolderId: number, revision: number | undefined) => {
      const url = `${baseUrl}/Copy`;
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ applicationId, targetFolderId, revision })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      console.debug('application.copy response', data);
      return data;
    },

    /**
     * Rename application.
     * @param applicationId Application GUID
     * @param name New name for Application
     * @returns
     */
    rename: async (applicationId: string, name?: string, label?: string) => {
      const url = `${baseUrl}/Rename`;
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ applicationId, name, label })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      console.debug('application.rename response', response);
      return response;
    },

    /**
     * Move application to given folder.
     * @param applicationId Application GUID
     * @param targetFolderId Folder to move Application to
     * @returns
     */
    move: async (applicationId: string, targetFolderId: number) => {
      const url = `${baseUrl}/Move`;
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ applicationId, targetFolderId })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      console.debug('application.move response', response);
      return response;
    },

    /**
     * Removes application. Note: This does not invalidate active releases.
     * @param applicationId Application GUID
     * @returns
     */
    remove: async (applicationId: string) => {
      const url = `${baseUrl}/Remove`;
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ applicationId })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      console.debug('application.remove response', response);
      return response;
    },

    /**
     * Restore removed application.
     * @param applicationId Application GUID
     * @returns
     */
    restore: async (applicationId: string) => {
      const url = `${baseUrl}/Restore`;
      const response = await fetchWithAuth(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ applicationId })
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      console.debug('application.restore response', response);
      return response;
    }
  };
};
